import React from "react";
import { Invoice, Claim } from "../../interfaces/screens/billing";
import { FormBuilder } from "../form/FormBuilder";
import { FormComponent } from "../../shared/utils/enums";

interface ClaimEditorProps {
  claim?: Claim;
  invoice?: Invoice;
  onClose: () => void;
}

const ClaimEditor: React.FC<ClaimEditorProps> = ({
  claim,
  invoice,
  onClose,
}) => {
  const handleFormSubmit = (formData: any) => {
    const finalClaim = {
      ...claim,
      ...formData,
      // If creating from an invoice, add invoice details
      ...(invoice && {
        invoice_id: invoice.id,
        total_amount_billed: invoice.total_amount,
      }),
    };
    // Here you would dispatch an action to save the claim
    onClose();
  };

  const claimFormSchema = {
    type: "object",
    properties: {
      // Basic Information
      total_amount_billed: {
        label: "Total Billed",
        component: FormComponent.Display,
        type: "number",
        required: false,
        componentProps: {},
      },
      
      // Insurer Information
      insurer_id: {
        label: "Insurer",
        component: FormComponent.Select,
        type: "string",
        required: false,
        componentProps: {
          // options would be populated from a redux state or API call
        },
      },
      
      // NSSF & Lebanese Insurance Information
      nssf_number: {
        label: "NSSF Number",
        component: FormComponent.Input,
        type: "string",
        required: false,
        componentProps: {},
      },
      patient_family_registration_id: {
        label: "Family Registration ID",
        component: FormComponent.Input,
        type: "string",
        required: false,
        componentProps: {},
      },
      policy_number: {
        label: "Policy Number",
        component: FormComponent.Input,
        type: "string",
        required: true,
        componentProps: {},
      },
      group_number: {
        label: "Group Number",
        component: FormComponent.Input,
        type: "string",
        required: false,
        componentProps: {},
      },
      employer_name: {
        label: "Employer Name",
        component: FormComponent.Input,
        type: "string",
        required: false,
        componentProps: {},
      },
      employer_registration_number: {
        label: "Employer Registration Number",
        component: FormComponent.Input,
        type: "string",
        required: false,
        componentProps: {},
      },
      patient_relationship_to_insured: {
        label: "Relationship to Insured",
        component: FormComponent.Select,
        type: "string",
        required: false,
        options: [
          { value: "self", label: "Self" },
          { value: "spouse", label: "Spouse" },
          { value: "child", label: "Child" },
          { value: "parent", label: "Parent" },
          { value: "sibling", label: "Sibling" },
          { value: "dependent", label: "Dependent" },
        ],
        componentProps: {},
      },
      
      // Submission Details
      submission_type: {
        label: "Submission Type",
        component: FormComponent.Select,
        type: "string",
        required: true,
        defaultValue: "paper",
        options: [
          { value: "paper", label: "Paper" },
          { value: "electronic", label: "Electronic" },
        ],
        componentProps: {},
      },
      submission_date: {
        label: "Submission Date",
        component: FormComponent.DatePicker,
        type: "string",
        required: true,
        componentProps: {},
      },
      insurer_reference_number: {
        label: "Insurer Reference Number",
        component: FormComponent.Input,
        type: "string",
        required: false,
        componentProps: {},
      },
      
      // Financial Information
      amount_covered_by_insurer: {
        label: "Amount Covered by Insurer",
        component: FormComponent.Input,
        type: "number",
        required: false,
        componentProps: {
          type: "number",
          min: 0,
          step: 0.01,
        },
      },
      amount_paid_by_insurer: {
        label: "Amount Paid by Insurer",
        component: FormComponent.Input,
        type: "number",
        required: false,
        componentProps: {
          type: "number",
          min: 0,
          step: 0.01,
        },
      },
      patient_copay: {
        label: "Patient Co-pay",
        component: FormComponent.Input,
        type: "number",
        required: false,
        componentProps: {
          type: "number",
          min: 0,
          step: 0.01,
        },
      },
      
      // Lebanon-specific Financial Breakdown
      nssf_coverage_amount: {
        label: "NSSF Coverage Amount",
        component: FormComponent.Input,
        type: "number",
        required: false,
        componentProps: {
          type: "number",
          min: 0,
          step: 0.01,
        },
      },
      moph_coverage_amount: {
        label: "MOPH Coverage Amount",
        component: FormComponent.Input,
        type: "number",
        required: false,
        componentProps: {
          type: "number",
          min: 0,
          step: 0.01,
        },
      },
      private_insurance_coverage_amount: {
        label: "Private Insurance Coverage Amount",
        component: FormComponent.Input,
        type: "number",
        required: false,
        componentProps: {
          type: "number",
          min: 0,
          step: 0.01,
        },
      },
      patient_responsibility_amount: {
        label: "Patient Responsibility Amount",
        component: FormComponent.Input,
        type: "number",
        required: false,
        componentProps: {
          type: "number",
          min: 0,
          step: 0.01,
        },
      },
      
      // Adjudication Details
      claim_status: {
        label: "Status",
        component: FormComponent.Select,
        type: "string",
        required: true,
        defaultValue: "draft",
        options: [
          { value: "draft", label: "Draft" },
          { value: "submitted", label: "Submitted" },
          { value: "under_review", label: "Under Review" },
          { value: "approved", label: "Approved" },
          { value: "partially_approved", label: "Partially Approved" },
          { value: "rejected", label: "Rejected" },
          { value: "paid", label: "Paid" },
          { value: "denied", label: "Denied" },
          { value: "pending_documentation", label: "Pending Documentation" },
        ],
        componentProps: {},
      },
      adjudication_date: {
        label: "Adjudication Date",
        component: FormComponent.DatePicker,
        type: "string",
        required: false,
        componentProps: {},
      },
      date_paid: {
        label: "Date Paid",
        component: FormComponent.DatePicker,
        type: "string",
        required: false,
        componentProps: {},
      },
      
      // Appeal Information
      appeal_deadline: {
        label: "Appeal Deadline",
        component: FormComponent.DatePicker,
        type: "string",
        required: false,
        componentProps: {},
      },
      appeal_status: {
        label: "Appeal Status",
        component: FormComponent.Select,
        type: "string",
        required: false,
        options: [
          { value: "pending", label: "Pending" },
          { value: "submitted", label: "Submitted" },
          { value: "approved", label: "Approved" },
          { value: "rejected", label: "Rejected" },
        ],
        componentProps: {},
      },
      
      // Textarea fields
      rejection_reason: {
        label: "Rejection Reason",
        component: FormComponent.Textarea,
        type: "string",
        required: false,
        componentProps: {
          rows: 3,
        },
      },
    },
  };

  const initialValues = claim || {
    invoice_id: invoice?.id,
    patient_id: invoice?.patient_id,
    total_amount_billed: invoice?.total_amount,
    amount_covered_by_insurer: 0,
    amount_paid_by_insurer: 0,
    patient_copay: 0,
    nssf_coverage_amount: 0,
    moph_coverage_amount: 0,
    private_insurance_coverage_amount: 0,
    patient_responsibility_amount: 0,
    // You would fetch patient's insurance details here to pre-populate
    // nssf_number, policy_number, etc.
  };

  return (
    <div className="pt-6">
      <FormBuilder
        schema={claimFormSchema}
        initialValues={initialValues}
        onSubmit={handleFormSubmit}
        buttonType="cancel-save"
      />
    </div>
  );
};

export default ClaimEditor;
